import React, { useState } from 'react';
import { X, Key, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { usePurchase } from '../../context/PurchaseContext';

export function TesterCodeModal({ isOpen, onClose }) {
  const { activateTesterMode } = usePurchase();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter a tester code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/tester/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data = await response.json();

      if (data.valid) {
        setSuccess(true);
        activateTesterMode(data.expires_at);
        
        // Close modal after short delay
        setTimeout(() => {
          onClose();
          window.location.reload(); // Refresh to update UI
        }, 2000);
      } else {
        setError(data.message || 'Invalid tester code');
      }
    } catch (err) {
      console.error('Error validating code:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Key className="w-5 h-5 text-primary" />
            </div>
            <h2 className="font-heading font-bold text-xl text-text">
              Enter Tester Code
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {success ? (
          // Success state
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-success/10 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h3 className="font-heading font-bold text-lg text-success mb-2">
              Access Granted!
            </h3>
            <p className="text-text-muted">
              Your tester access has been activated. Redirecting...
            </p>
          </div>
        ) : (
          // Form state
          <form onSubmit={handleSubmit}>
            <p className="text-text-muted mb-4">
              Enter your tester code to get temporary access to all course modules.
            </p>

            <Input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError('');
              }}
              placeholder="Enter your tester code"
              className="mb-4 h-12 text-center font-mono text-lg tracking-wider"
              disabled={isLoading}
              data-testid="tester-code-input"
            />

            {error && (
              <div className="flex items-center gap-2 text-error text-sm mb-4 bg-error/10 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading || !code.trim()}
              data-testid="submit-tester-code"
              className="w-full bg-primary text-white hover:bg-primary-600 rounded-xl py-5 font-bold"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                'Activate Tester Access'
              )}
            </Button>

            <p className="text-text-muted text-xs text-center mt-4">
              Tester access is valid for 7 days and is for evaluation purposes only.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
